import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { CrmNotification, fetchNotifications, markNotificationRead } from '../api/Notification';
import { AuthContext } from './AuthContext';

type NotificationContextValue = {
  notifications: CrmNotification[];
  unreadCount: number;
  markRead: (id: number) => Promise<void>;
};

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);
const POLL_MS = 30_000;

function playChime() {
  try {
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    const context = new AudioContextCtor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    gain.gain.setValueAtTime(0.14, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.45);
    oscillator.connect(gain); gain.connect(context.destination); oscillator.start(); oscillator.stop(context.currentTime + 0.45);
  } catch { /* Browser audio policies can block automatic playback. */ }
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const auth = useContext(AuthContext) as { user?: { id?: number } } | undefined;
  const [notifications, setNotifications] = useState<CrmNotification[]>([]);
  const seenIds = useRef<Set<number>>(new Set());
  const initialLoad = useRef(true);

  const refresh = useCallback(async () => {
    if (!auth?.user?.id) return;
    try {
      const next = await fetchNotifications();
      if (!initialLoad.current) {
        next.filter((notification) => !seenIds.current.has(notification.id)).forEach((notification) => {
          toast.info(`${notification.title}: ${notification.body}`, { autoClose: 7000 });
          playChime();
        });
      }
      seenIds.current = new Set(next.map((notification) => notification.id));
      initialLoad.current = false;
      setNotifications(next);
    } catch {
      // Notification polling must never interrupt normal CRM use.
    }
  }, [auth?.user?.id]);

  useEffect(() => {
    initialLoad.current = true; seenIds.current.clear();
    void refresh();
    const interval = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(interval);
  }, [refresh]);

  const markRead = useCallback(async (id: number) => {
    await markNotificationRead(id);
    setNotifications((current) => current.map((notification) => notification.id === id ? { ...notification, readAt: new Date().toISOString() } : notification));
  }, []);

  return <NotificationContext.Provider value={{ notifications, unreadCount: notifications.filter((notification) => !notification.readAt).length, markRead }}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used inside NotificationProvider');
  return context;
}
